import * as React from 'react';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Select, Card as AntCard, Alert, Empty } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { useZoomAssignedGroupsQuery } from 'core/graphql';
import { useGroupSchedules, useZoomMutations } from 'core/hooks';
import type { IScheduleSlot } from 'core/hooks';
import { SCHEDULE_TIME_OPTIONS, slotKey } from 'core/utils/zoomSchedule';
import { WeekDaySelect } from 'atoms';

import { weekDays } from '../../../constants/common';

interface IDraftSlot {
  weekDay: number;
  runTime: string;
}

const byDayTime = (a: IDraftSlot, b: IDraftSlot): number =>
  a.weekDay - b.weekDay || a.runTime.localeCompare(b.runTime);

const signature = (slots: IDraftSlot[]): string =>
  slots
    .map((slot) => slotKey(slot.weekDay, slot.runTime))
    .sort()
    .join(',');

interface IGroupCardProps {
  groupId: string;
  groupName: string;
  zoomAccountId: string;
  serverSlots: IScheduleSlot[];
  /** slotKey → name of the group that already holds it. */
  takenSlots: Map<string, string>;
  busy: boolean;
  onSave: (groupId: string, accountId: string, slots: IDraftSlot[]) => void;
}

const GroupScheduleCard = memo(
  ({
    groupId,
    groupName,
    zoomAccountId,
    serverSlots,
    takenSlots,
    busy,
    onSave,
  }: IGroupCardProps) => {
    const { t } = useTranslation();
    const [slots, setSlots] = useState<IDraftSlot[]>(() =>
      serverSlots.map((slot) => ({
        weekDay: slot.weekDay,
        runTime: slot.runTime,
      })),
    );
    const [pendingDay, setPendingDay] = useState<number | null>(null);
    const [pendingTime, setPendingTime] = useState<string>();
    const [error, setError] = useState('');

    const sortedSlots = useMemo(() => [...slots].sort(byDayTime), [slots]);
    const dirty = signature(slots) !== signature(serverSlots);

    const addSlot = useCallback(() => {
      if (pendingDay === null || !pendingTime) {
        return;
      }
      const key = slotKey(pendingDay, pendingTime);
      if (slots.some((s) => slotKey(s.weekDay, s.runTime) === key)) {
        setError(t('pages.zoom.schedule.alreadyInGroup'));
        return;
      }
      const takenBy = takenSlots.get(key);
      if (takenBy) {
        setError(
          t('pages.zoom.schedule.conflict', {
            day: weekDays[pendingDay],
            time: pendingTime.slice(0, 5),
            group: takenBy,
          }),
        );
        return;
      }
      setSlots((prev) => [
        ...prev,
        { weekDay: pendingDay, runTime: pendingTime },
      ]);
      setPendingDay(null);
      setPendingTime(undefined);
      setError('');
    }, [pendingDay, pendingTime, slots, takenSlots, t]);

    const removeSlot = useCallback((key: string) => {
      setSlots((prev) =>
        prev.filter((s) => slotKey(s.weekDay, s.runTime) !== key),
      );
    }, []);

    return (
      <Card>
        <GroupName>{groupName}</GroupName>

        {sortedSlots.length ? (
          <Chips>
            {sortedSlots.map((slot) => {
              const key = slotKey(slot.weekDay, slot.runTime);
              return (
                <Chip key={key}>
                  <span>
                    {weekDays[slot.weekDay]} {slot.runTime.slice(0, 5)}
                  </span>
                  <ChipButton
                    type="button"
                    title={t('pages.zoom.schedule.removeSlot')}
                    disabled={busy}
                    onClick={() => removeSlot(key)}
                  >
                    <CloseOutlined />
                  </ChipButton>
                </Chip>
              );
            })}
          </Chips>
        ) : (
          <Muted>{t('pages.zoom.schedule.noSlots')}</Muted>
        )}

        <AddRow>
          <WeekDaySelect selected={pendingDay} onClick={setPendingDay} />
          <Select<string>
            showSearch
            style={{ width: 120 }}
            placeholder={t('pages.zoom.schedule.selectTime')}
            value={pendingTime}
            options={SCHEDULE_TIME_OPTIONS}
            onChange={(value) => setPendingTime(value)}
          />
          <Button
            onClick={addSlot}
            disabled={pendingDay === null || !pendingTime || busy}
          >
            {t('pages.zoom.schedule.addSlot')}
          </Button>
        </AddRow>

        {error ? (
          <Alert
            type="warning"
            showIcon
            message={error}
            style={{ marginTop: 8 }}
          />
        ) : null}

        <Footer>
          <Button
            type="primary"
            disabled={!dirty}
            loading={busy}
            onClick={() => onSave(groupId, zoomAccountId, slots)}
          >
            {t('pages.zoom.schedule.save')}
          </Button>
        </Footer>
      </Card>
    );
  },
);

export const Schedule = memo(() => {
  const { t } = useTranslation();

  const { data: groupsData, error: groupsError } = useZoomAssignedGroupsQuery({
    fetchPolicy: 'cache-and-network',
  });
  const {
    schedulesByGroupId,
    allSlots,
    error: schedulesError,
    refetch,
  } = useGroupSchedules();
  const mutations = useZoomMutations();

  const groups = useMemo(
    () =>
      (groupsData?.allGroups?.edges ?? [])
        .map((edge) => edge?.node)
        .filter((node): node is NonNullable<typeof node> => Boolean(node))
        .filter((node) => Boolean(node.zoomAccount)),
    [groupsData],
  );

  const screenError = groupsError?.message ?? schedulesError;
  const busy = mutations.setGroupSchedule.loading;

  const takenSlotsFor = useCallback(
    (groupId: string) => {
      const map = new Map<string, string>();
      allSlots.forEach((slot) => {
        if (slot.groupId !== groupId) {
          map.set(slotKey(slot.weekDay, slot.runTime), slot.groupName);
        }
      });
      return map;
    },
    [allSlots],
  );

  const onSave = useCallback(
    async (groupId: string, accountId: string, slots: IDraftSlot[]) => {
      const ok = await mutations.setGroupSchedule.run(
        groupId,
        accountId,
        slots.map((slot) => ({
          weekDay: slot.weekDay,
          runTime: slot.runTime,
        })),
      );
      if (ok) {
        refetch();
      }
    },
    [mutations, refetch],
  );

  return (
    <Wrapper>
      {screenError ? (
        <Alert
          type="error"
          showIcon
          message={screenError}
          style={{ marginBottom: 12 }}
        />
      ) : null}

      <Title>{t('pages.zoom.schedule.overview')}</Title>
      <OverviewGrid>
        {weekDays.map((dayName, dayIndex) => {
          const daySlots = allSlots
            .filter((slot) => slot.weekDay === dayIndex)
            .sort((a, b) => a.runTime.localeCompare(b.runTime));
          return (
            <OverviewColumn key={dayName}>
              <OverviewDay>{dayName}</OverviewDay>
              {daySlots.length ? (
                daySlots.map((slot) => (
                  <OverviewSlot key={slot.id}>
                    <OverviewTime>{slot.runTime.slice(0, 5)}</OverviewTime>
                    <OverviewGroup>{slot.groupName}</OverviewGroup>
                  </OverviewSlot>
                ))
              ) : (
                <OverviewEmpty>—</OverviewEmpty>
              )}
            </OverviewColumn>
          );
        })}
      </OverviewGrid>

      <Title>{t('pages.zoom.schedule.groupsTitle')}</Title>
      {!groups.length ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('pages.zoom.schedule.empty')}
        />
      ) : (
        <CardGrid>
          {groups.map((group) => {
            const serverSlots = schedulesByGroupId[group.id] ?? [];
            return (
              <GroupScheduleCard
                key={`${group.id}-${signature(serverSlots)}`}
                groupId={group.id}
                groupName={group.name}
                zoomAccountId={group.zoomAccount?.id ?? ''}
                serverSlots={serverSlots}
                takenSlots={takenSlotsFor(group.id)}
                busy={busy}
                onSave={onSave}
              />
            );
          })}
        </CardGrid>
      )}
    </Wrapper>
  );
});

const Wrapper = styled.div`
  width: 100%;
  padding: 16px 0;
`;

const Title = styled.h2`
  font-size: 16px;
  font-weight: 600;
  margin: 24px 0 12px;

  &:first-of-type {
    margin-top: 0;
  }
`;

const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
`;

const OverviewColumn = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px;
  min-height: 80px;
`;

const OverviewDay = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  text-align: center;
  margin-bottom: 8px;
`;

const OverviewSlot = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 4px 6px;
  margin-bottom: 6px;
`;

const OverviewTime = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #2563eb;
`;

const OverviewGroup = styled.div`
  font-size: 12px;
  color: #374151;
  word-break: break-word;
`;

const OverviewEmpty = styled.div`
  text-align: center;
  color: #cbd5e1;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 16px;
`;

const Card = styled(AntCard)`
  .ant-card-body {
    display: flex;
    flex-direction: column;
  }
`;

const GroupName = styled.div`
  font-weight: 600;
  margin-bottom: 10px;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 6px 3px 10px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 14px;
  font-size: 13px;
`;

const ChipButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #dbeafe;
    color: #ef4444;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const AddRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
`;

const Muted = styled.div`
  color: #9ca3af;
  font-size: 13px;
  margin-bottom: 10px;
`;
